import { cn } from '@/utils/utils';
import { Skeleton } from '@shelflix/ui-web/components/ui/skeleton';
import { useEffect, useRef, useState } from 'react';

interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** Fallback image URL when main image fails to load */
  fallbackSrc?: string;
  /** Additional className for the skeleton */
  skeletonClassName?: string;
  /** Additional className for the container */
  containerClassName?: string;
  /** Aspect ratio class (e.g., 'aspect-[9/16]', 'aspect-video') */
  aspectRatio?: string;
  /** Object fit mode */
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  /** Show skeleton while loading (only on first load, not on src changes) */
  showSkeleton?: boolean;
}

const DEFAULT_FALLBACK = 'https://shelflix.com.br/wp-content/uploads/2025/02/Grey_Orange-300x90.png';

/**
 * Extracts the base path from a URL, ignoring query params and hash.
 * Used to compare URLs that point to the same resource but have different presigned signatures.
 */
function getUrlBasePath(url: string | undefined): string {
  if (!url) return '';
  try {
    const urlObj = new URL(url);
    return urlObj.origin + urlObj.pathname;
  } catch {
    // If URL parsing fails, return the original string
    return url;
  }
}

/**
 * Image component that maintains the previous image until the new one is fully loaded.
 * Prevents layout shift and provides smooth transitions between images.
 * Also prevents unnecessary reloads when URLs change but point to the same resource
 * (e.g., presigned URLs with different signatures).
 */
export function ImageWithFallback({
  src,
  alt,
  className,
  fallbackSrc = DEFAULT_FALLBACK,
  skeletonClassName,
  containerClassName,
  aspectRatio,
  objectFit = 'contain',
  showSkeleton = true,
  ...props
}: ImageWithFallbackProps) {
  // Track the currently displayed src (only updates when image is loaded)
  const [displayedSrc, setDisplayedSrc] = useState<string | undefined>(undefined);
  // Track if we're loading a new image
  const [isLoading, setIsLoading] = useState(true);
  // Track if this is the first load (no previous image to show)
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  // Track the pending src base path (the base URL we're trying to load, without query params)
  const pendingBasePathRef = useRef<string>(getUrlBasePath(src));
  // Track the actual pending src (with query params) for loading
  const pendingSrcRef = useRef<string | undefined>(src);
  // Preload image ref
  const preloadImgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const newBasePath = getUrlBasePath(src);

    // If base path hasn't changed, just update the displayed src without triggering loading state
    // This handles the case where presigned URL signature changes but the image is the same
    if (newBasePath === pendingBasePathRef.current && displayedSrc !== undefined) {
      // Update the displayed src to use the new presigned URL (keeps it fresh)
      if (src) {
        setDisplayedSrc(src);
      }
      pendingSrcRef.current = src;
      return;
    }

    pendingBasePathRef.current = newBasePath;
    pendingSrcRef.current = src;

    if (!src) {
      setDisplayedSrc(fallbackSrc);
      setIsLoading(false);
      setIsFirstLoad(false);
      return;
    }

    setIsLoading(true);

    // Create a new Image to preload
    const img = new Image();
    preloadImgRef.current = img;

    img.onload = () => {
      // Only update if this is still the current pending src
      if (pendingSrcRef.current === src) {
        setDisplayedSrc(src);
        setIsLoading(false);
        setIsFirstLoad(false);
      }
    };

    img.onerror = () => {
      // Only update if this is still the current pending src
      if (pendingSrcRef.current === src) {
        setDisplayedSrc(fallbackSrc);
        setIsLoading(false);
        setIsFirstLoad(false);
      }
    };

    img.src = src;

    // Cleanup
    return () => {
      if (preloadImgRef.current) {
        preloadImgRef.current.onload = null;
        preloadImgRef.current.onerror = null;
      }
    };
  }, [src, fallbackSrc]);

  const objectFitClass = {
    contain: 'object-contain',
    cover: 'object-cover',
    fill: 'object-fill',
    none: 'object-none',
    'scale-down': 'object-scale-down',
  }[objectFit];

  return (
    <div className={cn('relative overflow-hidden', aspectRatio, containerClassName)}>
      {/* Skeleton - only shown on first load when no previous image exists */}
      {showSkeleton && isFirstLoad && isLoading && (
        <Skeleton className={cn('absolute inset-0 h-full w-full', skeletonClassName)} />
      )}

      {/* Loading indicator overlay - shown when updating existing image */}
      {!isFirstLoad && isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/20">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
        </div>
      )}

      {/* Image - always rendered if displayedSrc exists */}
      {displayedSrc && (
        <img
          src={displayedSrc}
          alt={alt}
          className={cn('h-full w-full transition-opacity duration-200', objectFitClass, className)}
          {...props}
        />
      )}
    </div>
  );
}
